import {
  CollectionDocument,
  CollectionTransferOutcome,
  CollectionMemberSummary,
  CollectionTransferResult,
  parseCollectionTransferResult,
} from '@qkindex/contracts';
import { createOperationId } from '../../domain/operationId';
import {
  acceptCollectionTransfer,
  cancelCollectionTransfer,
  expireCollectionTransfer,
  getCollectionTransfer,
  startCollectionTransfer,
} from '../../firebase/functions';
import { firebaseCollectionRepository } from '../../repositories/collectionRepository';

export interface CollectionTransferDependencies {
  createOperationId(): string;
  getCollection(collectionId: string): Promise<CollectionDocument | undefined>;
  get(collectionId: string): Promise<CollectionTransferResult | null>;
  start(request: {
    collectionId: string;
    toUserId: string;
    operationId: string;
    expectedCollectionRevision: number;
    expectedLifecycleVersion: number;
    expectedRecipientRevision: number;
    expectedRecipientMembershipVersion: number;
  }): Promise<CollectionTransferResult>;
  accept(request: TransferAction): Promise<CollectionTransferResult>;
  cancel(request: TransferAction): Promise<CollectionTransferResult>;
  expire(request: TransferAction): Promise<CollectionTransferResult>;
}

type TransferAction = {
  collectionId: string;
  transferId: string;
  operationId: string;
  expectedTransferRevision: number;
  expectedOutcome?: CollectionTransferOutcome;
};

const defaults = (): CollectionTransferDependencies => ({
  createOperationId,
  getCollection: (collectionId) =>
    firebaseCollectionRepository.get(collectionId),
  get: async (collectionId) => {
    const value = (await getCollectionTransfer({ collectionId })).data;
    return value === null ? null : parseCollectionTransferResult(value);
  },
  start: async (request) =>
    parseCollectionTransferResult(
      (await startCollectionTransfer(request)).data,
    ),
  accept: async (request) =>
    parseCollectionTransferResult(
      (await acceptCollectionTransfer(request)).data,
    ),
  cancel: async (request) =>
    parseCollectionTransferResult(
      (await cancelCollectionTransfer(request)).data,
    ),
  expire: async (request) =>
    parseCollectionTransferResult(
      (await expireCollectionTransfer(request)).data,
    ),
});

export const createCollectionTransferController = (
  dependencies: CollectionTransferDependencies = defaults(),
) => {
  const pending = new Map<string, { key: string; operationId: string }>();
  const operationId = (action: string, key: string): string => {
    const existing = pending.get(action);
    if (existing?.key === key) return existing.operationId;
    const created = dependencies.createOperationId();
    pending.set(action, { key, operationId: created });
    return created;
  };
  return {
    get: dependencies.get,
    async start(collectionId: string, recipient: CollectionMemberSummary) {
      const collection = await dependencies.getCollection(collectionId);
      if (
        !collection ||
        collection.lifecycle !== 'active' ||
        collection.activeTransferId !== undefined ||
        recipient.status !== 'active' ||
        (recipient.role !== 'editor' && recipient.role !== 'administrator')
      )
        throw new Error(
          'Collection or recipient changed. Refresh and try again.',
        );
      const key = `${collectionId}\0${recipient.userId}\0${collection.revision}\0${collection.lifecycleVersion}\0${recipient.revision}\0${recipient.membershipVersion}`;
      return dependencies.start({
        collectionId,
        toUserId: recipient.userId,
        operationId: operationId('start', key),
        expectedCollectionRevision: collection.revision,
        expectedLifecycleVersion: collection.lifecycleVersion,
        expectedRecipientRevision: recipient.revision,
        expectedRecipientMembershipVersion: recipient.membershipVersion,
      });
    },
    mutate(
      action: 'accept' | 'cancel' | 'expire',
      transfer: CollectionTransferResult,
    ) {
      const key = `${transfer.collectionId}\0${transfer.transferId}\0${transfer.revision}\0${JSON.stringify(transfer.outcome)}`;
      return dependencies[action]({
        collectionId: transfer.collectionId,
        transferId: transfer.transferId,
        operationId: operationId(action, key),
        expectedTransferRevision: transfer.revision,
        ...(action === 'accept' ? { expectedOutcome: transfer.outcome } : {}),
      });
    },
    acknowledge(action: 'start' | 'accept' | 'cancel' | 'expire') {
      pending.delete(action);
    },
    reset() {
      pending.clear();
    },
  };
};

const nestedCode = (error: unknown, depth = 0): string | undefined => {
  if (!error || typeof error !== 'object' || depth > 3) return undefined;
  const value = error as {
    code?: unknown;
    details?: { code?: unknown };
    cause?: unknown;
  };
  if (typeof value.details?.code === 'string') return value.details.code;
  return (
    nestedCode(value.cause, depth + 1) ||
    (typeof value.code === 'string' ? value.code : undefined)
  );
};

export const collectionTransferErrorMessage = (error: unknown): string => {
  const code = nestedCode(error);
  if (code?.includes('capacity')) {
    return 'The recipient does not have capacity for this collection.';
  }
  if (
    code?.includes('operation-conflict') ||
    code?.includes('transfer-conflict')
  ) {
    return 'The transfer changed. Refresh and try again.';
  }
  if (
    code?.includes('role-required') ||
    code?.includes('membership-required')
  ) {
    return 'You are no longer authorized to manage this transfer.';
  }
  if (code?.includes('collection-unavailable')) {
    return 'Transfer is unavailable. Check collection availability and finish any pending work, then retry.';
  }
  if (
    error instanceof Error &&
    error.message.startsWith('Collection or recipient changed.')
  ) {
    return error.message;
  }
  return 'Unable to update the transfer. Check your connection and try again.';
};
